import { useEffect, useRef } from "react"

const COVER_PAGE_PANEL_FIELD_FOCUS_REQUEST_EVENT =
  "cover-page:panel-field-focus-request"

// An opening panel moves focus to its first control shortly after it mounts.
// The requested field holds focus through that window.
const FOCUS_HOLD_MS = 600
const FIELD_LOOKUP_ATTEMPTS = 20
const FIELD_LOOKUP_INTERVAL_MS = 50

// Held until the panel form that owns the field mounts and takes it.
let pendingField: string | null = null

/**
 * Ask the design panel to focus the form field with this name. Call it from
 * the click that opens the panel; it works whether or not the panel is open.
 */
export function requestCoverPagePanelFieldFocus(field: string) {
  if (typeof window === "undefined") return
  pendingField = field
  window.dispatchEvent(new Event(COVER_PAGE_PANEL_FIELD_FOCUS_REQUEST_EVENT))
}

/**
 * Attach the returned ref to a panel form. A field inside it takes focus when
 * {@link requestCoverPagePanelFieldFocus} names it.
 */
export function useCoverPagePanelFieldFocus<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    let release = () => {}
    let retry = 0
    const focusPendingField = (attemptsLeft = FIELD_LOOKUP_ATTEMPTS) => {
      window.clearTimeout(retry)
      if (!pendingField) return
      const field = ref.current?.querySelector<HTMLElement>(
        `[name="${CSS.escape(pendingField)}"]`
      )
      if (!field) {
        // The form can mount before its fields have loaded.
        if (attemptsLeft > 0)
          retry = window.setTimeout(
            () => focusPendingField(attemptsLeft - 1),
            FIELD_LOOKUP_INTERVAL_MS
          )
        return
      }
      pendingField = null
      release()

      const focus = () => field.focus({ preventScroll: true })
      // Take focus back if the opening panel moves it; stop as soon as the
      // person interacts, so this never fights a deliberate focus change.
      const onFocusIn = (event: FocusEvent) => {
        if (event.target !== field) focus()
      }
      const timeout = window.setTimeout(() => release(), FOCUS_HOLD_MS)
      release = () => {
        window.clearTimeout(timeout)
        document.removeEventListener("focusin", onFocusIn)
        document.removeEventListener("pointerdown", release)
        document.removeEventListener("keydown", release)
        release = () => {}
      }
      document.addEventListener("focusin", onFocusIn)
      document.addEventListener("pointerdown", release)
      document.addEventListener("keydown", release)
      focus()
    }
    const onRequest = () => focusPendingField()
    focusPendingField()
    window.addEventListener(
      COVER_PAGE_PANEL_FIELD_FOCUS_REQUEST_EVENT,
      onRequest
    )
    return () => {
      window.clearTimeout(retry)
      release()
      window.removeEventListener(
        COVER_PAGE_PANEL_FIELD_FOCUS_REQUEST_EVENT,
        onRequest
      )
    }
  }, [])
  return ref
}
